/**
 * getTimeZoneDateRange.js
 * 
 * Returns start and end dates for a given date range, properly adjusted for timezone.
 * 
 * FIX: End date now uses end of day (23:59:59.999) in the specified timezone.
 * This ensures "Today" at 8pm EST includes data through 8pm EST, not 7pm EST.
 */

const getTimeZoneDateRange = (timezone = 'America/New_York', range = '30d') => {
  // Get current date/time
  const now = new Date();
  
  // Create formatter for the timezone to get local date parts
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  // Parse the formatted date to get local date components
  const parts = formatter.formatToParts(now);
  const getPart = (type) => parts.find(p => p.type === type)?.value;
  
  const localYear = parseInt(getPart('year'));
  const localMonth = parseInt(getPart('month')) - 1; // JS months are 0-indexed
  const localDay = parseInt(getPart('day'));
  
  // End date: end of day in local timezone (let — reassigned by yesterday/custom date cases)
  let endDate = new Date(localYear, localMonth, localDay, 23, 59, 59, 999);
  
  // Calculate start date based on range
  let startDate;
  
  switch (range) {
    case 'today':
      startDate = new Date(localYear, localMonth, localDay, 0, 0, 0, 0);
      break;
    // NEW: Yesterday — full day (midnight to 11:59 PM) for the day before today
    case 'yesterday':
      startDate = new Date(localYear, localMonth, localDay - 1, 0, 0, 0, 0);
      endDate = new Date(localYear, localMonth, localDay - 1, 23, 59, 59, 999);
      break;
    case 'wtd': // Week to date (Monday start)
      const dayOfWeek = new Date(localYear, localMonth, localDay).getDay();
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(localYear, localMonth, localDay - mondayOffset, 0, 0, 0, 0);
      break;
    case 'mtd': // Month to date
      startDate = new Date(localYear, localMonth, 1, 0, 0, 0, 0);
      break;
    case '7d':
      startDate = new Date(localYear, localMonth, localDay - 6, 0, 0, 0, 0);
      break;
    case '30d':
      startDate = new Date(localYear, localMonth, localDay - 29, 0, 0, 0, 0);
      break;
    case '90d':
      startDate = new Date(localYear, localMonth, localDay - 89, 0, 0, 0, 0);
      break;
    case 'all':
      startDate = new Date(2020, 0, 1, 0, 0, 0, 0);
      break;
    default:
      // NEW: If range looks like a date string (YYYY-MM-DD), treat as a single-day pick
      if (/^\d{4}-\d{2}-\d{2}$/.test(range)) {
        const [y, m, d] = range.split('-').map(Number);
        startDate = new Date(y, m - 1, d, 0, 0, 0, 0);
        endDate = new Date(y, m - 1, d, 23, 59, 59, 999);
      } else {
        // Original fallback: Last 30 Days
        startDate = new Date(localYear, localMonth, localDay - 29, 0, 0, 0, 0);
      }
  }
  
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  };
};

export default getTimeZoneDateRange;